/**
 * Service silo data — the source of truth for every SEO landing page.
 *
 * Each service has:
 *   - slug, name, silo  (routing + categorisation)
 *   - intro / deepDive  (long-form copy for parent pages)
 *   - localIntro(city)  (city-injected intro for service+city pages)
 *   - howItWorks        (4 steps, service-flavoured)
 *   - whyChoose         (6 reasons, includes 1-2 service-unique)
 *   - faqs              (parent-page FAQ set, 6 service-unique)
 *   - localFaqs(city)   (city-flavoured FAQ pair, returns 2 extra Q&As)
 *   - related           (4 sibling service slugs in same silo)
 *   - keywordTerms      (used to seed meta keywords)
 *   - icon              (lucide-react name)
 *
 * The content here is template-driven with service- and city-specific variation
 * baked in (Areas Covered, neighbourhoods, distances, FAQ pairs) so each
 * rendered page exceeds the 30% unique-content threshold for SEO defensibility.
 */

// ---- Reusable building blocks ----------------------------------------------

const SHARED_HOW_IT_WORKS_TAIL = [
  {
    title: "Live tracking from collection to delivery",
    body: "Once your driver is on the way you get live ETA updates and a map link so you can see exactly where your transport is. No silent windows, no guesswork.",
  },
  {
    title: "Proof of delivery saved to your account",
    body: "Your driver captures a photo and signature at drop-off. Both are uploaded automatically and saved against your booking so you have a permanent audit trail.",
  },
];

const SHARED_WHY_CHOOSE = [
  {
    title: "Fully insured transport",
    body: "Every job is covered by comprehensive goods-in-transit insurance from collection to delivery.",
  },
  {
    title: "Transparent instant pricing",
    body: "The price you see on the quote is the price you pay. No call-out fees, no fuel surcharges, no surprises at the door.",
  },
  {
    title: "24/7 booking, same-day capacity",
    body: "Book any hour of the day or night. Same-day collections are available across all major UK metros, subject to driver availability.",
  },
  {
    title: "Vetted, professional drivers",
    body: "Every driver on the platform has had their licence, insurance and vehicle suitability checked before they ever pick up a job.",
  },
];

const SHARED_FAQS = [
  {
    q: "How quickly can you collect?",
    a: "Same-day collections are available across all major UK metropolitan areas, subject to driver availability at the time you book. For most jobs in standard working hours we can have a driver to you within two to four hours.",
  },
  {
    q: "Are my items insured during transit?",
    a: "Yes. Every job booked through Transport Anything 247 is covered by comprehensive goods-in-transit insurance, included as standard in the quoted price.",
  },
  {
    q: "How is the price calculated?",
    a: "Pricing is calculated transparently from the pickup and delivery postcodes, the size and type of item, and the vehicle class required. You see the full price before you book — no hidden fees, no minimum spend.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept all major credit and debit cards through Stripe's secure payment platform. Your card details never touch our servers.",
  },
];

// ---- Service silos ---------------------------------------------------------

export const SILOS = {
  vehicle: {
    key: "vehicle",
    label: "Vehicle Transport",
    description: "Dedicated transport for cars, motorcycles, vans, trailers and other vehicles across the UK.",
  },
  delivery: {
    key: "delivery",
    label: "Delivery",
    description: "Same-day and scheduled delivery for parcels, furniture, appliances and large items.",
  },
  removals: {
    key: "removals",
    label: "Removals",
    description: "House, flat and office removals with vetted, insured man-and-van crews.",
  },
};

// Helper for assembling a 6-item Why Choose Us list
const compose = (sharedHead, unique) => [...sharedHead, ...unique];

// ---- Service definitions ---------------------------------------------------
export const SERVICES = {
  // === VEHICLE TRANSPORT ===================================================
  "car-transport": {
    slug: "car-transport",
    name: "Car Transport",
    silo: "vehicle",
    icon: "Car",
    plural: "cars",
    h1Parent: "Car Transport Across the UK",
    h1Local: (city) => `Car Transport in ${city.name}`,
    titleParent: "Car Transport UK | Dedicated, Insured, Same-Day | Transport Anything 247",
    metaDescParent: "Professional car transport across the UK. Dedicated covered or open transporters, fully insured, same-day collection available. Instant transparent quote online or call +44 7757 133163.",
    metaDescLocal: (city) =>
      `Professional car transport in ${city.name}. Door-to-door vehicle collection and delivery, fully insured, same-day available. Instant transparent quote online or call +44 7757 133163.`,
    intro:
      "Whether you have just bought a car at auction, need to move a non-runner to your driveway, or are relocating a fleet vehicle between depots, Transport Anything 247 handles UK car transport with the care, paperwork and insurance cover the job deserves. We move running and non-running vehicles, prestige and standard models, single cars and small batches — all on dedicated transporters with no shared loads.",
    deepDive:
      "Our drivers operate flatbed transporters, low loaders and enclosed trailers depending on the vehicle's value, ride height and condition. Non-runners are winched on with hydraulic recovery kit; prestige and modified cars travel in covered transporters to protect against stone-chips and weather. Every collection includes pre-transport condition photography, a digital inspection report signed by you, and goods-in-transit insurance from the moment your keys are handed over until delivery is confirmed. We collect from auction sites, dealerships, private addresses, ports, salvage yards and storage facilities, and deliver to any UK address that's accessible to a transporter. Routes are planned around live traffic, ferry timings and ULEZ / Clean Air Zone charges, all of which are included in the quoted price.",
    localIntro: (city) =>
      `Need a car collected or delivered in ${city.name}? Whether you have just bought a vehicle at auction, are relocating across the country, or need a non-runner moved from a residential street, Transport Anything 247 provides dedicated car transport in ${city.name} with full goods-in-transit insurance and transparent instant pricing. Our drivers know ${city.regionShort || city.region} well — including the access challenges around ${city.transportContext}.`,
    howItWorks: [
      {
        title: "Get your instant quote",
        body: "Enter the pickup and delivery postcodes plus the vehicle make and model. Our calculator priced the job in seconds — no callbacks, no broker chains.",
      },
      {
        title: "Confirm your collection slot",
        body: "Pick a date and time window that suits you. We confirm the assigned transporter and driver within hours of booking, with full contact details.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Runners and non-runners alike",
          body: "Drive-on, winch-on or flat-towed — we handle vehicles in any condition, including salvage and accident-damaged cars.",
        },
        {
          title: "Open or enclosed transporters",
          body: "Standard cars travel on open flatbeds; prestige, classic and modified vehicles are moved in fully enclosed transporters at your request.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Do you transport non-running cars?",
        a: "Yes. Non-runners are loaded with hydraulic winches on flatbed transporters. We also handle vehicles with flat batteries, no keys, seized brakes, or accident damage — please let us know the condition when you book so we send the right transporter.",
      },
      {
        q: "Can you collect from a car auction?",
        a: "Yes. We collect from every major UK auction house — Copart, BCA, Manheim, ASM, Aston Barclay and the independents. Send us the auction reference and pickup window when you book and we will coordinate directly with the auction team.",
      },
      {
        q: "Are prestige cars transported in enclosed transporters?",
        a: "Enclosed transport is available on request and recommended for any vehicle with a value over £40,000, or for classic and modified cars where weather and stone-chip protection matter. Pricing for enclosed transport is shown alongside the standard quote so you can choose.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `How much does car transport from ${city.name} cost?`,
        a: `Prices depend on the distance, vehicle type and whether you need open or enclosed transport. As a guide, single-vehicle moves from ${city.name} to anywhere in the UK are calculated transparently from the postcodes and vehicle profile — you see the full price on the quote screen before booking. Most ${city.name} jobs fall within a clear, predictable banding based on miles, with no broker mark-up.`,
      },
      {
        q: `Do you collect from auctions and dealerships in ${city.name}?`,
        a: `Yes. We regularly collect from auction houses, dealerships and private sellers across ${city.region}. Just provide the pickup address, vehicle reference and a contact name and we will arrange the rest. Most ${city.name} collections can be done same-day or next working day.`,
      },
    ],
    related: ["vehicle-transport", "motorcycle-transport", "van-transport", "car-recovery-transport"],
    keywordTerms: ["car transport", "vehicle delivery", "car collection", "non-runner transport", "car shipping UK"],
  },

  "vehicle-transport": {
    slug: "vehicle-transport",
    name: "Vehicle Transport",
    silo: "vehicle",
    icon: "Truck",
    plural: "vehicles",
    h1Parent: "Vehicle Transport Across the UK",
    h1Local: (city) => `Vehicle Transport in ${city.name}`,
    titleParent: "Vehicle Transport UK | Cars, Vans, Bikes & Trailers | Transport Anything 247",
    metaDescParent: "UK-wide vehicle transport for cars, vans, motorcycles and trailers. Dedicated transporters, fully insured, instant transparent pricing. Call +44 7757 133163.",
    metaDescLocal: (city) =>
      `Vehicle transport in ${city.name} — cars, vans, motorcycles and trailers collected and delivered with full insurance. Instant transparent quote online or call +44 7757 133163.`,
    intro:
      "If it has wheels, we can move it. Transport Anything 247 provides dedicated vehicle transport across the UK for cars, vans, motorcycles, plant equipment, caravans and trailers. Single units or small batches, runners or non-runners, standard or specialist — all handled on dedicated transporters with no shared loads or broker chains.",
    deepDive:
      "Vehicle transport is one of those categories where the wrong equipment can damage a vehicle in seconds — wrong tie-down points, wrong ramp angle, wrong axle weight. We send the right transporter for the job: flatbed recovery trucks for standard cars and vans, low loaders for low-clearance and lowered vehicles, enclosed transporters for prestige and classic cars, and dedicated motorcycle transport with anchor points front and rear. Every booking includes pre-transport photography, a written condition report, and goods-in-transit insurance from the moment of collection until confirmed delivery. We work with dealerships, auction houses, salvage yards, ports, ferry terminals, race teams, fleet managers and private buyers across the UK.",
    localIntro: (city) =>
      `Need to move a vehicle to or from ${city.name}? Whether it's a single car bought at auction, a van being relocated between sites, or a motorcycle that needs collecting from a private seller, Transport Anything 247 provides dedicated vehicle transport in ${city.name} on the right equipment for the job. Our drivers handle the access challenges typical of ${city.region}, including ${city.transportContext}.`,
    howItWorks: [
      {
        title: "Quote in seconds",
        body: "Tell us the vehicle make, model and approximate condition, plus the pickup and delivery postcodes. The quote is calculated transparently with no broker mark-up.",
      },
      {
        title: "We send the right transporter",
        body: "Cars on flatbeds, prestige in enclosed, motorcycles on anchor-pointed trailers, plant on low-loaders — we match the equipment to your vehicle.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Specialist equipment per vehicle",
          body: "Flatbed, low-loader, enclosed or motorcycle-specific — we never compromise by sending the wrong transporter.",
        },
        {
          title: "Pre-transport condition reports",
          body: "Every collection includes photography and a signed condition report so disputes never arise about pre-existing damage.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "What types of vehicles can you transport?",
        a: "Cars (running or non-running, standard or prestige), motorcycles, vans, caravans, trailers, jet-skis, ride-on plant, and most other wheeled vehicles. If you are unsure whether your vehicle is within our scope, drop us a line on +44 7757 133163 and we will confirm before you book.",
      },
      {
        q: "Do you transport non-runners?",
        a: "Yes. Non-running cars, vans, motorcycles and salvage vehicles are loaded with hydraulic winches on flatbed or low-loader transporters. Please flag any non-runner status at the time of booking so we send the right equipment.",
      },
      {
        q: "Can I track my vehicle in transit?",
        a: "Yes. From the moment your driver accepts the job you can see their live location on the map view of your booking. ETA updates flow automatically as conditions change.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Do you cover all postcodes in ${city.name}?`,
        a: `Yes — every postcode in and around ${city.name} is covered, including the surrounding ${city.region} area. Common collection points like ${city.postcodeOutwardSamples.slice(0, 3).join(", ")} are reached daily.`,
      },
      {
        q: `How long does vehicle transport from ${city.name} to other UK cities take?`,
        a: `Most single-vehicle moves from ${city.name} are completed same-day or next working day. Cross-country routes (such as ${city.name} to Scotland or the South Coast) are typically next-day, with an exact ETA confirmed once your transporter is allocated.`,
      },
    ],
    related: ["car-transport", "motorcycle-transport", "van-transport", "trailer-transport"],
    keywordTerms: ["vehicle transport", "car delivery", "vehicle shipping UK", "vehicle collection"],
  },

  "motorcycle-transport": {
    slug: "motorcycle-transport",
    name: "Motorcycle Transport",
    silo: "vehicle",
    icon: "Bike",
    plural: "motorcycles",
    h1Parent: "Motorcycle Transport Across the UK",
    h1Local: (city) => `Motorcycle Transport in ${city.name}`,
    titleParent: "Motorcycle Transport UK | Bike Delivery & Collection | Transport Anything 247",
    metaDescParent: "Dedicated motorcycle transport across the UK. Anchor-point trailers, full insurance, same-day collection available. Instant transparent quote online.",
    metaDescLocal: (city) =>
      `Motorcycle transport in ${city.name} — sportbikes, cruisers, scooters and adventure bikes collected and delivered fully insured. Instant quote online.`,
    intro:
      "Moving a motorcycle is not the same as moving a car. The wrong tie-down compresses forks, the wrong angle scrapes pipes, and the wrong trailer can lay your pride and joy on its side. Transport Anything 247 provides dedicated motorcycle transport on bike-specific trailers with anchor points front and rear, soft tie-downs, and drivers who ride themselves and know how to handle a bike properly.",
    deepDive:
      "We move sportbikes, cruisers, naked bikes, adventure bikes, scooters, mopeds, classic and custom builds, race bikes between circuits, and barn-find restoration projects. Equipment varies by job: open trailers for standard runners with weather-resistant covers, enclosed trailers for prestige and race bikes, soft-strap tie-downs that do not compress forks, and wheel-chocks that lock the front wheel into a known position. Non-running bikes are loaded with bike-specific ramps and dolly-wheels. We collect from dealerships, auction houses, ports, race circuits, salvage yards and private addresses across the UK, and deliver to any address accessible to a trailer.",
    localIntro: (city) =>
      `Need to move a motorcycle to or from ${city.name}? Whether you are a track-day rider getting your bike to ${city.region}, a buyer who has just won an online auction, or a dealer relocating stock, Transport Anything 247 provides dedicated motorcycle transport in ${city.name} on bike-specific trailers with proper anchor points and soft tie-downs.`,
    howItWorks: [
      {
        title: "Quote with bike details",
        body: "Tell us the make, model and approximate weight, plus pickup and delivery postcodes. We confirm the right trailer for your bike instantly.",
      },
      {
        title: "Dedicated bike trailer assigned",
        body: "Anchor points front and rear, soft tie-downs, wheel chock, weather cover — every bike is loaded by a driver who rides themselves.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Bike-specific trailers only",
          body: "Anchor points, wheel chocks and soft straps — never car-style ratchets that compress forks.",
        },
        {
          title: "Drivers who ride themselves",
          body: "Every motorcycle assignment goes to a driver who rides — they know how to hold a bike, where the lift-points are, and how it should sit on the trailer.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Will my forks be compressed?",
        a: "No. We use soft tie-downs anchored to the bike's frame, not ratchet straps over the handlebars. Forks stay extended and your fork seals stay healthy.",
      },
      {
        q: "Can you transport sportbikes and race bikes?",
        a: "Yes. We transport racing and trackday bikes regularly, including bikes prepared with no plates and no road-legal kit. Please let us know if the bike has special mounting points (race stands, anchor blocks) at the time of booking.",
      },
      {
        q: "Do you carry classic and pre-war motorcycles?",
        a: "Yes, and they are often the most rewarding jobs. Enclosed trailers are recommended for classic bikes to protect against weather and road debris.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Do you collect from motorcycle dealers and auctions in ${city.name}?`,
        a: `Yes. We collect from every major motorcycle dealer in ${city.name} and ${city.region}, and from online auction sites that operate in the area. Just send us the dealer or auction reference and pickup window.`,
      },
      {
        q: `Can I track my bike on its journey from ${city.name}?`,
        a: `Yes — from the moment your driver accepts the job, you can see the bike's live location on the map view of your booking and receive automatic ETA updates.`,
      },
    ],
    related: ["car-transport", "vehicle-transport", "trailer-transport", "van-transport"],
    keywordTerms: ["motorcycle transport", "bike delivery", "motorbike shipping UK", "bike collection"],
  },

  "van-transport": {
    slug: "van-transport",
    name: "Van Transport",
    silo: "vehicle",
    icon: "Truck",
    plural: "vans",
    h1Parent: "Van Transport Across the UK",
    h1Local: (city) => `Van Transport in ${city.name}`,
    titleParent: "Van Transport UK | Panel Vans, Lutons & Tippers | Transport Anything 247",
    metaDescParent: "Dedicated van transport across the UK. Panel vans, Lutons, tippers and chassis-cab — collected and delivered with full insurance. Instant quote online.",
    metaDescLocal: (city) =>
      `Van transport in ${city.name} — small vans to chassis-cabs collected and delivered fully insured. Instant transparent quote online.`,
    intro:
      "Vans are workhorses, and moving one between sites should not stop your business. Transport Anything 247 provides dedicated van transport across the UK for panel vans, Lutons, tippers, chassis-cabs and refrigerated vans — single units or fleets, runners or non-runners, with the right transporter every time.",
    deepDive:
      "Different van classes need different equipment. Small vans (Berlingos, Combos, Caddy) ride comfortably on a standard flatbed. Mid-size vans (Transit Customs, Vivaros, Trafics) need a longer-deck transporter. Lutons and large LCVs need a dedicated long-loader with extended ramp angles. Tippers and chassis-cabs need axle-weight-rated low loaders. We send the right transporter for your van class and condition, and we handle vans with seized brakes, dead batteries or accident damage as long as they can be winched. Goods-in-transit insurance is included on every booking.",
    localIntro: (city) =>
      `Need a van moved to or from ${city.name}? Transport Anything 247 provides dedicated van transport in ${city.name} for all van classes — from car-derived vans up to chassis-cabs. Whether you are relocating a single Transit between depots or moving a fleet, we deliver across ${city.region} with full insurance and transparent instant pricing.`,
    howItWorks: [
      {
        title: "Quote by van class",
        body: "Tell us the make, model and trim. We confirm whether a flatbed, long-deck or low-loader is needed and price the job in seconds.",
      },
      {
        title: "Right transporter dispatched",
        body: "From small vans on flatbeds to chassis-cabs on heavy-duty low-loaders, we send the correct equipment first time.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "All van classes covered",
          body: "Car-derived vans, panel vans, Lutons, tippers and chassis-cabs — one provider, one quote, one driver.",
        },
        {
          title: "Fleet relocation handled",
          body: "Multiple vans between depots? We can sequence the collections to fit your operational schedule with no shutdown impact.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Do you transport vans with refrigeration or specialist conversions?",
        a: "Yes. We transport refrigerated vans, welfare vans, mobile workshops, ambulance conversions and specialist body conversions. Please let us know the conversion type when you book so we send a transporter with enough deck height and axle rating.",
      },
      {
        q: "Can you collect from a leasing or dealership depot?",
        a: "Yes. We work regularly with leasing companies (Arval, Lex, ALD, Alphabet), dealerships and fleet managers across the UK. Most depot collections can be booked with just a reference number and a contact name.",
      },
      {
        q: "Can fleet relocations be sequenced?",
        a: "Yes. If you have multiple vans to move between sites, we can plan the route and timings to minimise operational impact. Get in touch on +44 7757 133163 with the schedule and we will build a fleet plan around it.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Do you cover van transport across all of ${city.name}?`,
        a: `Yes. Every postcode in ${city.name} and the wider ${city.region} area is covered. Common collection points around ${city.postcodeOutwardSamples.slice(0, 3).join(", ")} are reached daily.`,
      },
      {
        q: `How quickly can a van be collected in ${city.name}?`,
        a: `Same-day collections are routinely available in ${city.name} during working hours, subject to driver availability at booking time. Most jobs booked before midday are collected the same afternoon.`,
      },
    ],
    related: ["car-transport", "vehicle-transport", "trailer-transport", "office-equipment-delivery"],
    keywordTerms: ["van transport", "van delivery", "panel van transport", "luton transport UK"],
  },

  "trailer-transport": {
    slug: "trailer-transport",
    name: "Trailer Transport",
    silo: "vehicle",
    icon: "Container",
    plural: "trailers",
    h1Parent: "Trailer Transport Across the UK",
    h1Local: (city) => `Trailer Transport in ${city.name}`,
    titleParent: "Trailer Transport UK | Box, Plant & Specialist Trailers | Transport Anything 247",
    metaDescParent: "Dedicated trailer transport across the UK — box, plant, horse, livestock and specialist trailers collected and delivered fully insured. Instant quote online.",
    metaDescLocal: (city) =>
      `Trailer transport in ${city.name} — box, plant, horse and specialist trailers collected and delivered. Fully insured, instant transparent quote.`,
    intro:
      "Trailers come in every shape and size and not every transporter can carry every trailer. Transport Anything 247 moves box trailers, plant trailers, horse trailers, livestock trailers, twin-axle car trailers, tipper trailers and specialist trailers across the UK on the right equipment for the job — single units or batches, loaded or unloaded.",
    deepDive:
      "We carry trailers in three modes: piggy-backed on a flatbed transporter (most common, for small to mid-size trailers under the GVW limit), towed behind a recovery vehicle (for trailers that are road-legal but not registered to you), or loaded on a low-loader (for high-value or unusually large trailers where road-towing risks damage). Every collection includes pre-transport photography, condition assessment, and goods-in-transit insurance covering the trailer and any contents agreed at booking time.",
    localIntro: (city) =>
      `Need a trailer moved to or from ${city.name}? Whether it's a horse trailer being relocated between stables, a plant trailer leaving an auction site, or a box trailer changing owner, Transport Anything 247 provides dedicated trailer transport in ${city.name} on the right equipment for your trailer's size and value.`,
    howItWorks: [
      {
        title: "Quote with trailer details",
        body: "Tell us the trailer type, approximate weight and dimensions. We confirm the right method (piggy-back, tow, or low-loader) and price instantly.",
      },
      {
        title: "Right method dispatched",
        body: "Small trailers ride on a flatbed; mid-size trailers are towed; oversize or high-value trailers travel on a dedicated low-loader.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Every trailer class covered",
          body: "Box, plant, horse, livestock, tipper, twin-axle car, specialist — one provider, one quote.",
        },
        {
          title: "Loaded or unloaded transport",
          body: "We can move trailers loaded (subject to weight and contents declaration) or unloaded — your call at booking time.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Can you transport loaded trailers?",
        a: "Yes — subject to a weight and contents declaration at the time of booking so we send a transporter with the right axle rating, and so the load is correctly insured. Please flag any contents at booking; we may need to confirm specific insurance cover for high-value contents.",
      },
      {
        q: "Do you transport horse trailers and livestock trailers?",
        a: "Yes. Horse and livestock trailers are transported unloaded as a general rule (live animals need a dedicated transporter operator licence). Trailers themselves are moved without the animals, which gives you full flexibility for the transport timing.",
      },
      {
        q: "What about plant trailers loaded with machinery?",
        a: "We move loaded plant trailers regularly — diggers, dumpers, compressors, generators. Please provide the trailer GVW and the loaded weight at booking, and a low-loader will be dispatched if the combination exceeds standard flatbed limits.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Where can a trailer be collected from in ${city.name}?`,
        a: `Anywhere with reasonable access for a transporter — auction sites, dealerships, stables, private addresses, industrial estates. We can quote any postcode in ${city.name} and the wider ${city.region} area.`,
      },
      {
        q: `Do you transport trailers from ${city.name} to Scotland or Northern Ireland?`,
        a: `Yes. Mainland UK routes (including Scotland) are handled directly by our drivers. Northern Ireland is supported via ferry routing — we will quote the full door-to-door price including the ferry leg.`,
      },
    ],
    related: ["car-transport", "vehicle-transport", "van-transport", "caravan-transport"],
    keywordTerms: ["trailer transport", "trailer delivery", "trailer collection UK", "plant trailer transport"],
  },

  "boat-transport": {
    slug: "boat-transport",
    name: "Boat Transport",
    silo: "vehicle",
    icon: "Sailboat",
    plural: "boats",
    h1Parent: "Boat Transport Across the UK",
    h1Local: (city) => `Boat Transport in ${city.name}`,
    titleParent: "Boat Transport UK | Speedboats, Yachts & RIBs | Transport Anything 247",
    metaDescParent: "Dedicated boat transport across the UK. Speedboats, yachts, RIBs, narrowboats and jet-skis collected and delivered with full insurance. Instant quote.",
    metaDescLocal: (city) =>
      `Boat transport in ${city.name} — speedboats, yachts, RIBs and jet-skis collected and delivered with full insurance. Instant transparent quote.`,
    intro:
      "Boats are unusually shaped, surprisingly fragile and need experienced handling on the road. Transport Anything 247 provides dedicated boat transport across the UK on flatbed or low-loader transporters with experienced operators who understand the difference between a cradle, a saddle and a sling.",
    deepDive:
      "Most boats up to about 30 feet ride on their existing trailer and travel on our flatbed transporters. Larger hulls travel on dedicated low-loaders with cradles or supports built up to match the hull profile. Narrowboats and barges are moved as oversize loads with appropriate escort arrangements. RIBs, speedboats, sailing dinghies and jet-skis are quick straightforward jobs. Yachts and motorboats with masts down ride on flatbeds; masts off require coordination with the marina. All boats are covered by our goods-in-transit insurance, and we will gladly liaise with your marina or boatyard on access and crane requirements.",
    localIntro: (city) =>
      `Need a boat moved to or from ${city.name}? Whether you are buying a RIB at a coastal auction, relocating a yacht to a different marina, or transporting a narrowboat from inland waterways, Transport Anything 247 provides dedicated boat transport in ${city.name} with experienced operators and the right cradles for your hull.`,
    howItWorks: [
      {
        title: "Quote with hull details",
        body: "Tell us the length, beam and draft, plus the marina or yard at each end. We confirm the right method and equipment instantly.",
      },
      {
        title: "Right transporter and cradle",
        body: "Smaller boats on flatbeds with their existing trailers; larger hulls on low-loaders with custom cradles. Mast-down or mast-off as agreed at booking.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Hull-specific cradles",
          body: "We build cradles to match the hull profile so weight is distributed evenly and the gel coat stays unscratched.",
        },
        {
          title: "Marina and yard liaison",
          body: "We coordinate with marinas and boatyards on crane access, slot times and tide-dependent windows so the boat arrives at exactly the right moment.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Do you transport sailing yachts with the mast up?",
        a: "Only very small dinghies are transported with the mast up. Larger yachts need the mast unstepped before road transport — we can coordinate with your marina on timing and crane access, but the mast unstep itself is normally booked directly with the marina.",
      },
      {
        q: "Can you transport narrowboats?",
        a: "Yes. Narrowboats are moved on dedicated low-loaders, often as oversize loads which require route planning and possibly escort vehicles. Quotes for narrowboats are confirmed manually after you submit details — please include the length, beam and current location.",
      },
      {
        q: "What insurance is included?",
        a: "Goods-in-transit insurance is included on every booking. For boats above £50,000 in value we recommend you confirm your existing hull insurance also covers road transport — most policies do, but it is worth checking.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Can you collect boats from marinas around ${city.name}?`,
        a: `Yes. We collect from inland marinas, dry-storage yards and coastal harbours within reach of ${city.name}. Please share the marina name and access details at booking and we will coordinate the slot directly.`,
      },
      {
        q: `Do you transport jet-skis in ${city.name}?`,
        a: `Yes — jet-skis are quick, straightforward jobs and ride on their existing trailers. Most ${city.name} jet-ski moves are completed same-day or next-day.`,
      },
    ],
    related: ["trailer-transport", "vehicle-transport", "caravan-transport", "large-item-delivery"],
    keywordTerms: ["boat transport", "boat delivery", "yacht transport UK", "RIB transport"],
  },

  "caravan-transport": {
    slug: "caravan-transport",
    name: "Caravan Transport",
    silo: "vehicle",
    icon: "Caravan",
    plural: "caravans",
    h1Parent: "Caravan Transport Across the UK",
    h1Local: (city) => `Caravan Transport in ${city.name}`,
    titleParent: "Caravan Transport UK | Static & Touring Caravans | Transport Anything 247",
    metaDescParent: "Dedicated caravan transport across the UK. Touring caravans, static caravans, twin-axles and motorhomes collected and delivered with full insurance.",
    metaDescLocal: (city) =>
      `Caravan transport in ${city.name} — touring caravans, statics and motorhomes collected and delivered fully insured. Instant transparent quote.`,
    intro:
      "Caravans are tall, light, and easily damaged by the wrong tie-down strap. Transport Anything 247 provides dedicated caravan transport across the UK for touring caravans, twin-axles, static caravans and motorhomes with experienced operators who know how to load and secure a caravan without scuffing the sides.",
    deepDive:
      "Touring caravans and twin-axles are typically towed behind a recovery vehicle on the road, with electrics and brakes checked before departure. Where the caravan is unregistered or non-running we move it piggy-backed on a flatbed transporter. Static caravans are oversize loads moved on dedicated low-loaders with route planning to avoid low bridges and tight residential turns; we coordinate with the destination park on access and siting. Motorhomes ride on their own wheels under their own power where possible, or on flatbeds when non-running. All caravans are covered by goods-in-transit insurance.",
    localIntro: (city) =>
      `Need a caravan moved to or from ${city.name}? Whether it's a touring caravan being relocated from a private address, a static heading to a holiday park, or a motorhome that needs collecting from a dealership, Transport Anything 247 provides dedicated caravan transport in ${city.name} with experienced operators familiar with ${city.region}'s caravan parks and holiday sites.`,
    howItWorks: [
      {
        title: "Quote by caravan type",
        body: "Tell us if it's touring, twin-axle, static or motorhome, and confirm pickup and delivery postcodes. We price by the right method in seconds.",
      },
      {
        title: "Right method dispatched",
        body: "Touring caravans towed; statics on low-loaders; motorhomes under their own power or on flatbeds for non-runners.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Static caravans handled",
          body: "Oversize-load route planning, low-bridge avoidance and holiday-park liaison all included as standard.",
        },
        {
          title: "Pre-transport checks",
          body: "Tyre pressures, brakes, lights and tie-down points all checked before the caravan moves an inch.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Do you move static caravans?",
        a: "Yes. Static caravans are oversize loads requiring route planning, low-bridge avoidance, and sometimes escort vehicles. Quotes for statics are confirmed manually after submission — please include the caravan length, width and exact addresses at both ends.",
      },
      {
        q: "Can you tow my touring caravan or do you put it on a transporter?",
        a: "Depending on the caravan and the route we either tow on the road behind a recovery vehicle, or load on a flatbed. The towed method is the standard option and is typically faster; the flatbed method is mandatory for caravans without registration or with mechanical issues.",
      },
      {
        q: "Will you liaise with the holiday park or storage site?",
        a: "Yes. We coordinate access slots and siting with the destination park directly when needed. Please share the park name and your booking reference at the time of order.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Do you cover caravan parks around ${city.name}?`,
        a: `Yes. We deliver to and collect from caravan parks, residential storage sites and private addresses across ${city.region}. Please share the destination park's exact address at booking so we can confirm access in advance.`,
      },
      {
        q: `How long does caravan transport from ${city.name} usually take?`,
        a: `Touring caravan moves from ${city.name} to anywhere on the UK mainland are typically completed within one to two working days. Static moves take longer due to oversize-load route planning.`,
      },
    ],
    related: ["trailer-transport", "vehicle-transport", "boat-transport", "van-transport"],
    keywordTerms: ["caravan transport", "static caravan transport", "motorhome transport UK", "touring caravan delivery"],
  },

  "car-recovery-transport": {
    slug: "car-recovery-transport",
    name: "Car Recovery Transport",
    silo: "vehicle",
    icon: "AlertTriangle",
    plural: "recoveries",
    h1Parent: "Car Recovery & Breakdown Transport Across the UK",
    h1Local: (city) => `Car Recovery in ${city.name}`,
    titleParent: "Car Recovery UK | 24/7 Breakdown & Accident Recovery | Transport Anything 247",
    metaDescParent: "24/7 car recovery and breakdown transport across the UK. Accident, non-runner and salvage recovery on dedicated flatbeds, fully insured. Call 24/7.",
    metaDescLocal: (city) =>
      `24/7 car recovery in ${city.name} — breakdown, accident and salvage recovery on flatbeds. Fully insured, fast response, transparent pricing.`,
    intro:
      "Cars break down at the worst times in the worst places, and a fast, calm response can be the difference between a small disruption and a wrecked day. Transport Anything 247 provides 24/7 car recovery and breakdown transport across the UK on dedicated flatbed transporters — for accidents, mechanical failures, key losses, salvage moves and any other situation where your car cannot safely drive itself.",
    deepDive:
      "Recovery jobs are different from scheduled transport. We respond within hours, often within the same hour for in-town jobs during working hours, and we handle the immediate logistics: making the vehicle safe at the roadside, loading it carefully on a hydraulic-winch flatbed, and delivering it to your home, garage, salvage yard or insurance assessor. Salvage and category-write-off vehicles are handled with the same care as runners — the condition of the car does not change the standard of service. Goods-in-transit insurance is included on every booking, and the price you see is the price you pay.",
    localIntro: (city) =>
      `Broken down in ${city.name}? Need a non-runner moved off your driveway? Transport Anything 247 provides 24/7 car recovery in ${city.name} on dedicated flatbed transporters. Whether it's an accident-damaged vehicle that needs collecting urgently, a non-runner that has been sitting for months, or a salvage vehicle heading to auction, we respond fast and handle the job calmly.`,
    howItWorks: [
      {
        title: "Get your quote (24/7)",
        body: "Tell us where the vehicle is and where it needs to go. Pricing is transparent and confirmed on the quote screen — no surprise call-out fees.",
      },
      {
        title: "Driver dispatched fast",
        body: "We dispatch the nearest available recovery flatbed and confirm an arrival ETA within minutes of booking.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "24/7 response, including weekends",
          body: "Recovery and breakdown jobs do not respect office hours. We operate every hour of every day.",
        },
        {
          title: "Roadside-safe loading",
          body: "Our drivers are trained to make the scene safe before they touch the vehicle — high-visibility cones, lighting and proper assessment.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "How quickly can you reach me?",
        a: "For in-town jobs during working hours we are typically with you within 1-2 hours of booking. Out-of-hours and rural jobs are confirmed with an ETA at the time of dispatch.",
      },
      {
        q: "Can you recover from a motorway?",
        a: "Yes, but motorway recoveries are subject to police and Highways England coordination. If you are on a live motorway, please call 999 first to make the scene safe, then call us to arrange transport once you are clear.",
      },
      {
        q: "Do you handle accident write-offs and category cars?",
        a: "Yes. Category A, B, S and N write-offs are moved on the same flatbeds as standard recoveries. We can deliver direct to your insurance assessor's yard or to a salvage auction if that's where the vehicle is heading.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Do you cover the whole of ${city.name} for recovery?`,
        a: `Yes. Every postcode in ${city.name} and the surrounding ${city.region} area is covered 24/7. We can be at most ${city.name} addresses within 1-2 hours during working hours.`,
      },
      {
        q: `Can you recover from car parks and private addresses in ${city.name}?`,
        a: `Yes — private driveways, supermarket car parks, multi-storey car parks (subject to height), workplace lots and on-street locations are all routinely handled.`,
      },
    ],
    related: ["car-transport", "vehicle-transport", "van-transport", "trailer-transport"],
    keywordTerms: ["car recovery", "breakdown recovery", "accident recovery", "non-runner recovery UK", "24/7 car recovery"],
  },

  // === DELIVERY =============================================================
  "furniture-delivery": {
    slug: "furniture-delivery",
    name: "Furniture Delivery",
    silo: "delivery",
    icon: "Sofa",
    plural: "furniture items",
    h1Parent: "Furniture Delivery Across the UK",
    h1Local: (city) => `Furniture Delivery in ${city.name}`,
    titleParent: "Furniture Delivery UK | Sofas, Beds & Wardrobes | Transport Anything 247",
    metaDescParent: "Professional furniture delivery across the UK. Sofas, beds, wardrobes and dining sets moved same-day with dedicated transport. Instant transparent quote.",
    metaDescLocal: (city) =>
      `Furniture delivery in ${city.name} — sofas, beds, wardrobes and dining sets delivered same-day with dedicated, insured transport. Instant quote online.`,
    intro:
      "Furniture is heavy, awkward and easily damaged through doorways, around bannisters and up stairwells. Transport Anything 247 provides dedicated furniture delivery across the UK — sofas, beds, wardrobes, dining sets, garden furniture, mirrors and artwork — with two-person crews where needed and proper protection on every item.",
    deepDive:
      "Most furniture jobs are single-item moves between marketplace seller and buyer (Facebook Marketplace, Gumtree, eBay), or new-purchase deliveries from independent furniture shops that do not run their own fleet. We pick up, wrap fragile surfaces with furniture blankets and stretch wrap, secure the load with non-marking straps, and place items in the room of choice at the destination — not just inside the front door. Two-person crews are dispatched automatically when an item's weight or size requires it (e.g. sofas, mattresses over king-size, large wardrobes). Goods-in-transit insurance is included on every booking.",
    localIntro: (city) =>
      `Need furniture moved in ${city.name}? Whether you've bought a sofa from a marketplace seller, ordered a new bed from a local showroom, or are moving a single piece between properties, Transport Anything 247 provides dedicated furniture delivery in ${city.name} with two-person crews when needed and proper protection on every item. Our drivers know the access challenges of ${city.region} — narrow staircases, walk-up flats, restricted parking — and plan accordingly.`,
    howItWorks: [
      {
        title: "Quote with item details",
        body: "Tell us what you're moving and the pickup and delivery postcodes. We confirm whether a two-person crew is needed and price the job in seconds.",
      },
      {
        title: "Right crew dispatched",
        body: "Single-driver for lighter items, two-person crew for sofas, mattresses, large wardrobes and anything requiring stair work.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Room of choice placement",
          body: "We place your item where you want it — not just inside the front door. Stair work and bannister navigation are included as standard.",
        },
        {
          title: "Furniture blankets and stretch wrap",
          body: "Every fragile surface is wrapped before it leaves the property so scratches and gouges never happen in transit.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Do you offer a two-person crew for heavy items?",
        a: "Yes. We dispatch a two-person crew automatically for sofas, mattresses larger than king-size, large wardrobes, dining tables seating six or more, and anything requiring stair work above the first floor. Just describe the item at booking — we will assess and confirm.",
      },
      {
        q: "Will you dismantle and reassemble furniture?",
        a: "Yes, on request. Wardrobes that won't fit through doorways and IKEA-style flat-pack items are routinely dismantled at one end and reassembled at the other. Add a note at booking and we will price it in.",
      },
      {
        q: "Do you deliver from Facebook Marketplace and Gumtree?",
        a: "Yes — marketplace deliveries are one of our most-common job types. We act as the middle leg: we collect from the seller, transport to the buyer, and confirm completion with photo evidence. You don't need to be present at the pickup if the seller is willing.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Do you deliver furniture across all of ${city.name}?`,
        a: `Yes. Every postcode in ${city.name} and the surrounding ${city.region} area is covered. Common collection areas around ${city.postcodeOutwardSamples.slice(0, 3).join(", ")} are reached daily.`,
      },
      {
        q: `Can you handle furniture delivery to walk-up flats in ${city.name}?`,
        a: `Yes. Walk-up flats with no lift are routine in ${city.name}, and our two-person crews handle stair work as standard. Please tell us the floor number at booking so we send the right crew.`,
      },
    ],
    related: ["large-item-delivery", "appliance-delivery", "piano-transport", "man-and-van"],
    keywordTerms: ["furniture delivery", "sofa delivery", "bed delivery", "marketplace delivery UK", "Gumtree delivery"],
  },

  "large-item-delivery": {
    slug: "large-item-delivery",
    name: "Large Item Delivery",
    silo: "delivery",
    icon: "Package2",
    plural: "large items",
    h1Parent: "Large Item Delivery Across the UK",
    h1Local: (city) => `Large Item Delivery in ${city.name}`,
    titleParent: "Large Item Delivery UK | Oversize Parcels & Goods | Transport Anything 247",
    metaDescParent: "Large item and oversize parcel delivery across the UK. Items couriers refuse — sofas, fridges, garden equipment, exercise bikes — delivered fully insured.",
    metaDescLocal: (city) =>
      `Large item delivery in ${city.name} — oversize parcels and goods that standard couriers refuse. Dedicated transport, fully insured, same-day available.`,
    intro:
      "Standard parcel couriers refuse anything over their weight or dimension limits — typically around 30kg or 1.2m long. Transport Anything 247 handles the rest. Garden machinery, exercise equipment, large mirrors, oversize parcels, palletised goods, hot tubs and anything else that won't fit in a Royal Mail or DPD network is moved on dedicated transport with proper protection.",
    deepDive:
      "We provide dedicated transport for items that exceed standard courier limits: oversize parcels, palletised goods, awkwardly-shaped items, fragile items requiring careful handling, and bulk shipments where a parcel network would be too expensive. Vehicles range from car-derived vans for medium items up to Lutons and 7.5-tonne curtain-siders for palletised loads. Two-person crews are available where needed for kerbside-to-room delivery, and tail-lifts are dispatched automatically when items exceed crew lift weight.",
    localIntro: (city) =>
      `Got an item that's too big for a standard courier in ${city.name}? Transport Anything 247 handles oversize parcels, palletised goods, garden equipment, exercise machines and anything else that exceeds DPD or Royal Mail limits. Same-day collection across ${city.name} and the wider ${city.region} area, with dedicated transport and full insurance included.`,
    howItWorks: [
      {
        title: "Quote with item dimensions",
        body: "Tell us the dimensions, approximate weight and whether a tail-lift or two-person crew is needed. We price by exact vehicle class required.",
      },
      {
        title: "Right vehicle and crew",
        body: "From car-derived vans for medium items up to 7.5-tonne curtain-siders for palletised loads — we send the right kit first time.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Items couriers refuse",
          body: "Over 30kg, over 1.2m, awkwardly shaped, fragile — if a parcel network won't take it, we will.",
        },
        {
          title: "Tail-lift and crew options",
          body: "Heavy items don't need to be ground-floor only. Tail-lifts and two-person crews are dispatched automatically when needed.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "What's the maximum size and weight you'll carry?",
        a: "Single items up to 7.5 tonnes and approximately 6m long can be handled directly. Above those limits we will quote manually — please call +44 7757 133163 with the exact dimensions and weight.",
      },
      {
        q: "Do you carry palletised goods?",
        a: "Yes. Standard, half and full pallets are routine. Tail-lift vehicles are dispatched automatically when a pallet exceeds two-person lift weight.",
      },
      {
        q: "Can you collect from a warehouse or industrial unit?",
        a: "Yes. We collect from warehouses, industrial estates, trade counters and self-storage facilities daily. Please provide the operating hours and access details at booking.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Do you cover warehouses and industrial estates in ${city.name}?`,
        a: `Yes. Industrial estates and trade counters around ${city.name} and ${city.region} are well-known to our drivers. Common collection areas around ${city.postcodeOutwardSamples.slice(0, 2).join(" and ")} are reached daily.`,
      },
      {
        q: `Is same-day large item delivery available in ${city.name}?`,
        a: `Yes — same-day capacity in ${city.name} is available subject to driver availability at the time you book. Most jobs booked before midday are collected the same afternoon.`,
      },
    ],
    related: ["furniture-delivery", "pallet-delivery", "appliance-delivery", "same-day-delivery"],
    keywordTerms: ["large item delivery", "oversize parcel delivery", "courier refused items UK", "big item delivery"],
  },

  "parcel-delivery": {
    slug: "parcel-delivery",
    name: "Parcel Delivery",
    silo: "delivery",
    icon: "Package",
    plural: "parcels",
    h1Parent: "Parcel Delivery Across the UK",
    h1Local: (city) => `Parcel Delivery in ${city.name}`,
    titleParent: "Parcel Delivery UK | Same-Day Dedicated Courier | Transport Anything 247",
    metaDescParent: "Same-day dedicated parcel delivery across the UK. Direct van service — no hubs, no sorting, no delay. Fully insured, transparent pricing.",
    metaDescLocal: (city) =>
      `Same-day parcel delivery in ${city.name} — dedicated direct courier, no hubs, no sorting. Fully insured, instant transparent quote.`,
    intro:
      "Standard parcel networks are economical but slow — your parcel goes to a hub, gets sorted, then re-distributed. For time-critical jobs that breaks down. Transport Anything 247 provides direct same-day dedicated parcel delivery across the UK — straight from your hand to the recipient with no hub, no sorting, no delay.",
    deepDive:
      "Dedicated parcel delivery is faster and more reliable than network couriers because the parcel never leaves the same vehicle from collection to delivery. Common use cases: urgent legal documents, medical samples, machine parts for production lines, missed wedding orders, time-critical eBay sales, contracts requiring same-day signature, and confidential commercial documents. Vehicles range from cars and small vans for documents through to Lutons for bulk parcel runs. Every job is tracked live with proof of delivery captured on the app.",
    localIntro: (city) =>
      `Need a parcel delivered same-day in ${city.name}? Whether it's an urgent business document, a missed Royal Mail item, or a time-critical eBay sale, Transport Anything 247 provides direct same-day parcel delivery in ${city.name} — no hubs, no sorting, no delay. Collection is typically within 1-2 hours of booking during working hours.`,
    howItWorks: [
      {
        title: "Book in 60 seconds",
        body: "Enter pickup and delivery postcodes and the parcel dimensions. Pricing is calculated transparently — typically less than a network express service for direct same-day.",
      },
      {
        title: "Direct collection within 1-2 hours",
        body: "Our nearest driver collects from you and heads straight to the destination — no depot detour, no sorting delay.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "No hubs, no sorting, no delay",
          body: "Direct point-to-point — your parcel rides in the same vehicle from collection to delivery.",
        },
        {
          title: "Faster than express networks",
          body: "Most same-day jobs are delivered within 4-6 hours of booking — quicker than any next-day or even pre-noon network promise.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "How fast is same-day delivery?",
        a: "Most same-day jobs are delivered within 4-6 hours of booking, depending on the route distance. Local moves (under 50 miles) are typically completed within 2-3 hours.",
      },
      {
        q: "What's the maximum parcel size?",
        a: "Single parcels up to van capacity are handled on a standard dedicated parcel service. Above that we move into our large-item or pallet category — please call +44 7757 133163 if unsure.",
      },
      {
        q: "Is my parcel insured?",
        a: "Yes. Goods-in-transit insurance is included on every job at no extra cost.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `How fast is same-day parcel delivery in ${city.name}?`,
        a: `Local same-day jobs within ${city.name} are typically completed within 2-3 hours of booking. Cross-city jobs (within ${city.region}) are usually completed the same afternoon.`,
      },
      {
        q: `Do you collect from businesses in ${city.name}?`,
        a: `Yes. We collect from offices, retail premises, professional services and trade counters across ${city.name} every working day. Operating hours and access details should be added at booking.`,
      },
    ],
    related: ["same-day-delivery", "large-item-delivery", "pallet-delivery", "office-equipment-delivery"],
    keywordTerms: ["parcel delivery", "same-day courier", "dedicated parcel service UK", "direct courier"],
  },

  "pallet-delivery": {
    slug: "pallet-delivery",
    name: "Pallet Delivery",
    silo: "delivery",
    icon: "Container",
    plural: "pallets",
    h1Parent: "Pallet Delivery Across the UK",
    h1Local: (city) => `Pallet Delivery in ${city.name}`,
    titleParent: "Pallet Delivery UK | Quarter, Half & Full Pallets | Transport Anything 247",
    metaDescParent: "Dedicated pallet delivery across the UK. Quarter, half and full pallets moved with tail-lift access, fully insured. Same-day collection available.",
    metaDescLocal: (city) =>
      `Pallet delivery in ${city.name} — quarter, half and full pallets moved fast with tail-lift access. Fully insured, instant transparent quote.`,
    intro:
      "Pallets are awkward, heavy and need the right vehicle and equipment. Transport Anything 247 provides dedicated pallet delivery across the UK on tail-lift vans and curtain-siders — quarter, half and full pallets, single units or batches, with full goods-in-transit insurance.",
    deepDive:
      "Pallet sizes vary: a quarter pallet (600×800mm, up to 250kg) fits on a tail-lift van; a half pallet (1000×1200mm, up to 500kg) needs a Luton with tail-lift; a full pallet (1000×1200mm at full GVW, up to 1000kg) needs a curtain-side or box vehicle. We assess automatically at booking and send the right vehicle — no return visits, no failed deliveries. We collect from warehouses, manufacturers, retailers and trade counters, and deliver to commercial and residential addresses across the UK with tail-lift access included as standard.",
    localIntro: (city) =>
      `Need a pallet delivered in ${city.name}? Transport Anything 247 provides dedicated pallet delivery in ${city.name} on tail-lift vehicles, with full insurance and transparent instant pricing. Whether you're a trade buyer collecting from a wholesaler or a business shipping to a customer, we handle the load.`,
    howItWorks: [
      {
        title: "Quote by pallet size",
        body: "Tell us if it's quarter, half or full pallet, and the approximate weight. We confirm vehicle class instantly.",
      },
      {
        title: "Tail-lift dispatched",
        body: "Every pallet job goes out on a tail-lift vehicle as standard. No surprises on arrival.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Tail-lift as standard",
          body: "Kerbside delivery is included on every pallet job — no surprise at the door.",
        },
        {
          title: "Same-day capacity",
          body: "Most pallet jobs booked before 10am are delivered the same day across the UK mainland.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "What are the typical pallet sizes?",
        a: "Quarter (600×800mm, ~250kg), half (1000×1200mm, ~500kg) and full (1000×1200mm at GVW, up to 1000kg). We assess and price by exact size at booking.",
      },
      {
        q: "Do you offer kerbside delivery only or to inside premises?",
        a: "Kerbside is included as standard. Inside-premises delivery (where pallet trucks can be wheeled inside) is available on request. Stair work and lift work for pallets is on a per-quote basis — please call +44 7757 133163.",
      },
      {
        q: "Is my pallet insured?",
        a: "Yes. Standard goods-in-transit insurance is included. For high-value pallets (over £25,000 declared value) please flag the contents at booking so additional cover is confirmed.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Do you cover trade counters and warehouses in ${city.name}?`,
        a: `Yes. Trade counters, wholesalers and warehouses across ${city.name} and ${city.region} are routine collection points. Please share operating hours and access details at booking.`,
      },
      {
        q: `How quickly can pallets be delivered in ${city.name}?`,
        a: `Local moves within ${city.name} are typically same-day. Cross-country jobs to and from ${city.name} are usually next working day at the latest.`,
      },
    ],
    related: ["large-item-delivery", "parcel-delivery", "office-equipment-delivery", "building-material-delivery"],
    keywordTerms: ["pallet delivery", "pallet courier", "tail-lift delivery UK", "trade pallet delivery"],
  },

  "same-day-delivery": {
    slug: "same-day-delivery",
    name: "Same-Day Delivery",
    silo: "delivery",
    icon: "Zap",
    plural: "same-day jobs",
    h1Parent: "Same-Day Delivery Across the UK",
    h1Local: (city) => `Same-Day Delivery in ${city.name}`,
    titleParent: "Same-Day Delivery UK | Urgent Direct Transport | Transport Anything 247",
    metaDescParent: "Same-day dedicated delivery across the UK. Direct van service, no hubs, no sorting. Fully insured, transparent instant pricing.",
    metaDescLocal: (city) =>
      `Same-day delivery in ${city.name} — direct dedicated transport, no hubs, no delay. Fully insured, instant transparent quote.`,
    intro:
      "Some jobs cannot wait until tomorrow. Transport Anything 247 provides same-day delivery across the UK for urgent parcels, documents, machine parts, medical samples, equipment and missed orders — direct from collection to destination with no hub, no sorting, no delay.",
    deepDive:
      "Same-day delivery is the right choice when the cost of failure exceeds the cost of dedicated transport. A production line down for a missing part costs thousands per hour — a same-day delivery of that part costs a fraction. We dispatch the nearest available driver within minutes of booking, collect within 1-2 working hours, and head direct to the destination. Vehicles are matched to the load: cars and small vans for documents and parcels, Lutons for larger goods, and HGVs for pallet-sized urgent freight. Every job is tracked live with proof of delivery.",
    localIntro: (city) =>
      `Need it delivered today in ${city.name}? Transport Anything 247 provides same-day dedicated delivery in ${city.name} for parcels, documents, machine parts, missed orders and any other urgent shipment. Collection within 1-2 working hours, no hubs, no delay, transparent pricing.`,
    howItWorks: [
      {
        title: "Book in 60 seconds",
        body: "Enter postcodes and item details. Pricing is calculated transparently — typically less than express network rates for direct same-day.",
      },
      {
        title: "Driver dispatched within minutes",
        body: "The nearest available driver is allocated and an ETA is confirmed within minutes of booking.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Direct point-to-point",
          body: "No hubs, no sorting, no delay — your shipment never leaves the same vehicle.",
        },
        {
          title: "24/7 booking",
          body: "Same-day capacity is available every working day, with out-of-hours options confirmed manually.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "How fast is same-day?",
        a: "Most local jobs (under 50 miles) are completed within 2-3 hours of booking. Regional jobs (under 200 miles) are typically completed within 4-6 hours.",
      },
      {
        q: "What's the cut-off time?",
        a: "There is no fixed cut-off — same-day capacity is checked live at booking. Most jobs booked before 2pm are completed the same day across the UK mainland.",
      },
      {
        q: "How much does same-day cost vs next-day networks?",
        a: "Dedicated same-day costs more than next-day networks but typically less than express same-day networks (which themselves only beat us by 1-2 hours). For urgent jobs the maths almost always favours direct dedicated transport.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `How fast is same-day delivery from ${city.name}?`,
        a: `Local jobs within ${city.name} are typically completed within 2-3 hours. Cross-country jobs from ${city.name} are usually completed by end of working day.`,
      },
      {
        q: `Do you operate at weekends in ${city.name}?`,
        a: `Yes — weekend same-day capacity is available in ${city.name}, though it may be slightly slower than weekday response depending on driver availability. Please book and we'll confirm the ETA.`,
      },
    ],
    related: ["parcel-delivery", "large-item-delivery", "office-equipment-delivery", "appliance-delivery"],
    keywordTerms: ["same day delivery", "urgent courier", "same-day dedicated UK", "express direct delivery"],
  },

  "appliance-delivery": {
    slug: "appliance-delivery",
    name: "Appliance Delivery",
    silo: "delivery",
    icon: "Refrigerator",
    plural: "appliances",
    h1Parent: "Appliance Delivery Across the UK",
    h1Local: (city) => `Appliance Delivery in ${city.name}`,
    titleParent: "Appliance Delivery UK | Fridges, Ovens & Washing Machines | Transport Anything 247",
    metaDescParent: "Professional appliance delivery across the UK. Fridges, washing machines, ovens and dishwashers delivered same-day with two-person crews.",
    metaDescLocal: (city) =>
      `Appliance delivery in ${city.name} — fridges, washers, ovens and dishwashers delivered with two-person crews, fully insured. Instant quote online.`,
    intro:
      "White goods are heavy, awkward and easily damaged if mishandled — and they need to be loaded upright or carefully laid down to protect compressors. Transport Anything 247 provides dedicated appliance delivery across the UK with two-person crews, proper trolleys, and drivers who know how to manoeuvre a 110kg American fridge-freezer up two flights of stairs without damaging the unit or the staircase.",
    deepDive:
      "We deliver every major appliance class: fridges and fridge-freezers (including American-style), washing machines, washer-dryers, tumble dryers, dishwashers, range cookers, built-in ovens and hobs, freezers, microwaves and small kitchen appliances. Two-person crews are dispatched automatically for items above standard lift weight. Tilt-and-trolley access is included as standard, and we leave fridges upright at the destination for 4 hours before plugging in (the standard manufacturer guidance) where requested. Goods-in-transit insurance covers the appliance from collection to delivery.",
    localIntro: (city) =>
      `Need an appliance delivered in ${city.name}? Whether you've bought a fridge-freezer from a marketplace seller, ordered a washing machine from a local appliance shop, or are moving white goods between properties, Transport Anything 247 provides dedicated appliance delivery in ${city.name} with two-person crews and proper protection.`,
    howItWorks: [
      {
        title: "Quote with appliance type",
        body: "Tell us what you're moving (fridge, washer, oven, etc.) and the pickup and delivery postcodes. Two-person crew is dispatched automatically when needed.",
      },
      {
        title: "Right crew and equipment",
        body: "Trolleys, stair-climbers, blankets, straps — every appliance is wrapped before it leaves the property and unwrapped on arrival.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Two-person crew as standard",
          body: "Every appliance over 60kg goes out with a two-person crew — no exceptions, no compromises.",
        },
        {
          title: "Manufacturer guidance respected",
          body: "We transport fridges upright wherever possible and leave them upright at the destination so compressors can settle before power-on.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Will you take away my old appliance?",
        a: "Yes, on request. We can collect the old unit at the same visit and dispose of it at a licensed recycling facility for a flat fee. Please flag this at booking so we send a vehicle with enough deck space for both items.",
      },
      {
        q: "Can you handle stair work for white goods?",
        a: "Yes. Two-person crews handle stair work as standard, including walk-up flats with no lift. Please tell us the floor number at booking so we send the right crew.",
      },
      {
        q: "Do you connect appliances at the destination?",
        a: "We deliver and place appliances in the room of choice but we do not connect water, gas or electrical supplies — those require qualified trades. We can recommend local Gas-Safe plumbers and electricians if needed.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Do you deliver appliances to walk-up flats in ${city.name}?`,
        a: `Yes. Walk-up flats are routine in ${city.name}, and our two-person crews handle stair work as standard. Please tell us the floor number at booking so we send a crew with stair-climbers if needed.`,
      },
      {
        q: `Can you collect appliances from showrooms in ${city.name}?`,
        a: `Yes. We collect from independent appliance shops, big-box retailers and trade counters across ${city.name} every working day. Just share the dealer reference and pickup window at booking.`,
      },
    ],
    related: ["furniture-delivery", "large-item-delivery", "office-equipment-delivery", "man-and-van"],
    keywordTerms: ["appliance delivery", "fridge delivery", "washing machine delivery UK", "white goods courier"],
  },

  "piano-transport": {
    slug: "piano-transport",
    name: "Piano Transport",
    silo: "delivery",
    icon: "Music",
    plural: "pianos",
    h1Parent: "Piano Transport Across the UK",
    h1Local: (city) => `Piano Transport in ${city.name}`,
    titleParent: "Piano Transport UK | Upright & Grand Piano Movers | Transport Anything 247",
    metaDescParent: "Specialist piano transport across the UK — upright, grand and digital pianos moved by trained crews. Fully insured, instant quote.",
    metaDescLocal: (city) =>
      `Piano transport in ${city.name} — upright, grand and digital pianos moved by trained crews. Fully insured, transparent instant quote.`,
    intro:
      "Pianos are heavy, fragile, and represent decades of musical sentiment — they need to be moved by people who have moved pianos before. Transport Anything 247 provides specialist piano transport across the UK with trained crews using piano dollies, skidboards, and proper rigging for upright, grand and digital pianos.",
    deepDive:
      "Upright pianos (200-300kg) are moved on piano dollies with two-person crews, wrapped in piano blankets and secured with non-slip straps. Grand pianos (350-550kg) are partially disassembled — legs and lyre removed — laid carefully on a piano board, wrapped, and moved on heavy-duty dollies with three-person crews where stairs are involved. Digital pianos are usually a two-person crew job depending on weight. Goods-in-transit insurance is included; for high-value or concert-grade instruments please flag at booking so additional cover is confirmed.",
    localIntro: (city) =>
      `Need a piano moved in ${city.name}? Whether it's an upright that's been in the family for generations, a grand piano coming from a music school, or a digital piano changing owner, Transport Anything 247 provides specialist piano transport in ${city.name} with trained crews and proper equipment.`,
    howItWorks: [
      {
        title: "Quote with piano type",
        body: "Tell us if it's upright, grand or digital, the approximate weight, and the pickup and delivery postcodes. We confirm crew size and equipment automatically.",
      },
      {
        title: "Specialist crew dispatched",
        body: "Two- or three-person crews with piano dollies, skidboards, blankets and proper rigging — never general couriers.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Trained piano crews",
          body: "Every piano job goes to a crew who has moved pianos before — not a generalist who will learn on yours.",
        },
        {
          title: "Grand piano disassembly",
          body: "Legs, lyre and pedals removed where needed for narrow doorways or tight staircases — and re-assembled on arrival.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Do you move grand pianos?",
        a: "Yes. Grand pianos are moved on dedicated piano boards with the legs and lyre removed for transport. Three-person crews are dispatched where stair work or tight access is involved.",
      },
      {
        q: "Will you handle tight staircases and narrow doorways?",
        a: "Yes. Piano moves involving stair work, narrow doorways and balcony access are routine. Please describe the access at booking — photos help us send the right crew and equipment first time.",
      },
      {
        q: "Is my piano insured during transport?",
        a: "Yes. Goods-in-transit insurance is included. For pianos with a value above £25,000 (concert pianos, antique instruments) please flag at booking so additional cover is confirmed before transport.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Can you handle piano moves in walk-up flats in ${city.name}?`,
        a: `Yes. Walk-up flats with no lift are common in ${city.name} and our crews handle them with proper stair-climbing equipment. Please tell us the floor number and provide photos of access at booking.`,
      },
      {
        q: `Do you tune the piano after delivery in ${city.name}?`,
        a: `We do not provide piano tuning ourselves but we can recommend local tuners in ${city.region} on request. Pianos typically need 2-4 weeks to settle in a new environment before tuning is worthwhile.`,
      },
    ],
    related: ["furniture-delivery", "large-item-delivery", "appliance-delivery", "man-and-van"],
    keywordTerms: ["piano transport", "piano movers", "upright piano delivery", "grand piano transport UK"],
  },

  "office-equipment-delivery": {
    slug: "office-equipment-delivery",
    name: "Office Equipment Delivery",
    silo: "delivery",
    icon: "Briefcase",
    plural: "office equipment loads",
    h1Parent: "Office Equipment Delivery Across the UK",
    h1Local: (city) => `Office Equipment Delivery in ${city.name}`,
    titleParent: "Office Equipment Delivery UK | Desks, Chairs & IT Kit | Transport Anything 247",
    metaDescParent: "Dedicated office equipment delivery across the UK. Desks, chairs, filing cabinets and IT kit moved with care. Fully insured, instant quote.",
    metaDescLocal: (city) =>
      `Office equipment delivery in ${city.name} — desks, chairs, filing cabinets and IT kit moved by dedicated, insured crews. Instant transparent quote.`,
    intro:
      "Office equipment is awkward, often fragile, and frequently needs delivering outside business hours. Transport Anything 247 provides dedicated office equipment delivery across the UK for desks, chairs, filing cabinets, IT kit, printers, monitors and audio-visual equipment — single items or whole-office shipments, in working hours or after.",
    deepDive:
      "Office equipment ranges from a single executive chair on a marketplace pick-up to a multi-pallet relocation. We handle both extremes. Single items are moved on car-derived or panel vans with single-driver service. Whole-office moves go on Lutons and 7.5-tonne curtain-siders with two- or three-person crews for assembly, disassembly and IT-safe handling. We work with facilities managers, IT teams and office fit-out contractors across the UK, including for evening and weekend slots that minimise business disruption.",
    localIntro: (city) =>
      `Need office equipment moved in ${city.name}? Whether it's a single executive chair from a marketplace seller, a full IT kit relocation, or an office fit-out where the kit needs to be in place before Monday, Transport Anything 247 provides dedicated office equipment delivery in ${city.name} — including evening and weekend slots that minimise business disruption.`,
    howItWorks: [
      {
        title: "Quote with equipment details",
        body: "Tell us what you're moving — single item, multi-piece, or full office relocation. We confirm vehicle class and crew size instantly.",
      },
      {
        title: "Right crew and timing",
        body: "Single items in working hours; whole-office moves at evenings or weekends; IT-sensitive kit handled by crews trained for it.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Evening and weekend slots",
          body: "Whole-office moves don't have to disrupt your business day. Out-of-hours slots are confirmed at booking.",
        },
        {
          title: "IT-safe handling",
          body: "Servers, monitors and AV kit are wrapped in anti-static blankets and secured with non-marking straps.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Can you assemble flat-pack office furniture on arrival?",
        a: "Yes, on request. Add a note at booking and we will price in the assembly time. Most flat-pack desks and pedestal units can be assembled in 15-30 minutes per piece.",
      },
      {
        q: "Do you handle whole-office relocations?",
        a: "Yes. Whole-office moves are usually planned 1-2 weeks in advance with a site visit beforehand. Please call +44 7757 133163 with your relocation date and approximate inventory.",
      },
      {
        q: "Is IT and AV equipment insured?",
        a: "Goods-in-transit insurance is included as standard. For high-value IT kit (server racks, large displays, AV systems above £10,000) please flag at booking so additional cover is confirmed.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Can you deliver office equipment to business addresses in ${city.name}?`,
        a: `Yes. Commercial premises across ${city.name} and ${city.region} are routine. Common business postcodes around ${city.postcodeOutwardSamples.slice(0, 2).join(" and ")} are reached daily, including evening slots where access is restricted in working hours.`,
      },
      {
        q: `Can you handle weekend office relocations in ${city.name}?`,
        a: `Yes. Weekend slots for whole-office moves in ${city.name} are routine and are usually the most cost-effective time to relocate — buildings are quieter, lifts are free, and the move team has the floor to themselves.`,
      },
    ],
    related: ["furniture-delivery", "pallet-delivery", "office-removals", "appliance-delivery"],
    keywordTerms: ["office equipment delivery", "office furniture delivery", "IT relocation UK", "office fit-out delivery"],
  },

  "building-material-delivery": {
    slug: "building-material-delivery",
    name: "Building Material Delivery",
    silo: "delivery",
    icon: "Hammer",
    plural: "building material loads",
    h1Parent: "Building Material Delivery Across the UK",
    h1Local: (city) => `Building Material Delivery in ${city.name}`,
    titleParent: "Building Material Delivery UK | Bricks, Timber & Plasterboard | Transport Anything 247",
    metaDescParent: "Dedicated building material delivery across the UK. Bricks, timber, plasterboard, insulation and aggregate moved to site, fully insured.",
    metaDescLocal: (city) =>
      `Building material delivery in ${city.name} — bricks, timber, plasterboard and aggregates moved to site, fully insured. Instant transparent quote.`,
    intro:
      "Building sites run on materials being in the right place at the right time. Transport Anything 247 provides dedicated building material delivery across the UK for bricks, timber, plasterboard, insulation, aggregate, plumbing supplies, electrical kit and bagged goods — single drops or recurring multi-drop runs, with tail-lift access included.",
    deepDive:
      "We handle palletised building materials on tail-lift Lutons and 7.5-tonne curtain-siders, plus loose materials (timber lengths, sheet plasterboard, insulation rolls) on flatbeds with strap-and-cover protection. We collect from builders' merchants (Travis Perkins, Jewson, Wickes, Selco, MKM, B&Q Trade), self-storage facilities, manufacturer trade counters, and direct from sites. Delivery is to active building sites with banksman coordination where required, or to residential and commercial premises with kerbside drop included.",
    localIntro: (city) =>
      `Need building materials delivered in ${city.name}? Whether it's a single pallet of plasterboard from a trade counter, a Luton of timber lengths from a merchant, or a multi-drop run between sites, Transport Anything 247 provides dedicated building material delivery in ${city.name} with tail-lift access and full insurance.`,
    howItWorks: [
      {
        title: "Quote by load type",
        body: "Tell us what you're moving (palletised, loose, sheet, bagged), pickup and delivery postcodes, and any access constraints. We confirm vehicle class instantly.",
      },
      {
        title: "Tail-lift dispatched",
        body: "Every palletised job goes out on a tail-lift as standard. Loose loads on flatbeds with covers and ratchet straps.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Site delivery experience",
          body: "Our drivers know how to navigate active building sites, deal with banksmen, and drop materials in the right zone.",
        },
        {
          title: "Multi-drop capability",
          body: "Recurring multi-drop runs between merchants and sites are planned around your schedule.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Do you collect from builders' merchants?",
        a: "Yes. We work daily with Travis Perkins, Jewson, Wickes, Selco, MKM, B&Q Trade and independent merchants across the UK. Just share the merchant reference and pickup window at booking.",
      },
      {
        q: "Can you deliver to active building sites?",
        a: "Yes. Site delivery is routine. Please share any site-induction requirements, banksman contact and site access details at booking so the driver arrives prepared.",
      },
      {
        q: "Do you carry loose timber and sheet plasterboard?",
        a: "Yes. Loose timber lengths and sheet plasterboard ride on flatbeds with covers and ratchet straps. Protected from weather and load-secured to current haulage standards.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Do you cover builders' merchants in ${city.name}?`,
        a: `Yes. Every major merchant in ${city.name} and ${city.region} is reached daily, including the trade counters around ${city.postcodeOutwardSamples.slice(0, 2).join(" and ")}.`,
      },
      {
        q: `Can you deliver to building sites across ${city.name}?`,
        a: `Yes. Active building sites in ${city.name} are routine — please share site contact, induction requirements and access window at booking so the driver arrives prepared.`,
      },
    ],
    related: ["pallet-delivery", "large-item-delivery", "office-equipment-delivery", "appliance-delivery"],
    keywordTerms: ["building material delivery", "merchants delivery", "site delivery UK", "construction materials transport"],
  },

  // === REMOVALS ============================================================
  "house-removals": {
    slug: "house-removals",
    name: "House Removals",
    silo: "removals",
    icon: "Home",
    plural: "house moves",
    h1Parent: "House Removals Across the UK",
    h1Local: (city) => `House Removals in ${city.name}`,
    titleParent: "House Removals UK | Local & Long-Distance | Transport Anything 247",
    metaDescParent: "Professional house removals across the UK. Local and long-distance moves with insured crews, packing supplies and same-day capacity.",
    metaDescLocal: (city) =>
      `House removals in ${city.name} — local and long-distance moves with insured, vetted crews. Transparent instant quote online.`,
    intro:
      "Moving house is one of the most stressful things people do, and the wrong removal company can multiply that stress for weeks. Transport Anything 247 provides dedicated house removals across the UK with vetted, insured crews, the right vehicles for your inventory, and transparent instant pricing — local moves around the corner or long-distance moves the length of the country.",
    deepDive:
      "House removals vary enormously: a one-bed flat move with minimal furniture is a 3-4 hour job on a Luton with a two-person crew; a four-bed house with garage and shed is a full day on a 7.5-tonne vehicle with a four-person crew, possibly with a return visit for the shed contents. We size the vehicle, crew and timing accurately at booking from your inventory description. Packing supplies (boxes, tape, blankets, mattress covers) can be supplied in advance, and full-pack service (we pack your house for you) is available on request. Goods-in-transit insurance is included on every booking.",
    localIntro: (city) =>
      `Moving house in ${city.name}? Whether you're going around the corner from one ${city.name} flat to another, or moving out of ${city.region} entirely, Transport Anything 247 provides dedicated house removals in ${city.name} with vetted, insured crews and transparent instant pricing.`,
    howItWorks: [
      {
        title: "Quote with property details",
        body: "Tell us the property size, inventory description and pickup and delivery postcodes. We size the vehicle, crew and timing at booking.",
      },
      {
        title: "Right crew and vehicle",
        body: "Luton with two-person crew for flats; 7.5-tonne with four-person crew for larger houses. Tail-lift access where needed.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Insured, vetted crews",
          body: "Every removal crew has had their driving licence, insurance and vehicle checked. Goods-in-transit insurance is included on every booking.",
        },
        {
          title: "Packing supplies and full-pack option",
          body: "We can supply boxes, tape, blankets and mattress covers in advance, or pack your house for you the day before the move.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Do you supply packing materials?",
        a: "Yes. We can deliver packing boxes, tape, bubble wrap and mattress covers in advance of your move date. Add the supplies at booking and we will price them in.",
      },
      {
        q: "Can you handle the packing for me?",
        a: "Yes. Our full-pack service means we arrive the day before your move and pack everything for you (you keep your kettle and toothbrush). Full-pack is priced per room and confirmed at booking.",
      },
      {
        q: "What about long-distance moves?",
        a: "Long-distance moves (over 200 miles) are typically completed in one day with an early start. Multi-day moves (Scotland to South Coast, or with storage in between) are planned with you at booking.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Do you cover all postcodes in ${city.name} for house removals?`,
        a: `Yes. Every postcode in ${city.name} and the surrounding ${city.region} area is covered. Common moves around ${city.postcodeOutwardSamples.slice(0, 3).join(", ")} are completed daily.`,
      },
      {
        q: `Can you handle walk-up flat removals in ${city.name}?`,
        a: `Yes. Walk-up flats are routine in ${city.name} and our four-person crews are dispatched automatically for top-floor flats with no lift. Please tell us the floor number at booking.`,
      },
    ],
    related: ["flat-removals", "office-removals", "man-and-van", "furniture-delivery"],
    keywordTerms: ["house removals", "house moving company", "house removals UK", "professional removals"],
  },

  "flat-removals": {
    slug: "flat-removals",
    name: "Flat Removals",
    silo: "removals",
    icon: "Building",
    plural: "flat moves",
    h1Parent: "Flat Removals Across the UK",
    h1Local: (city) => `Flat Removals in ${city.name}`,
    titleParent: "Flat Removals UK | Walk-Up & Lifted | Transport Anything 247",
    metaDescParent: "Professional flat removals across the UK. Walk-up and lifted flats moved with experienced crews. Transparent instant quote, same-day available.",
    metaDescLocal: (city) =>
      `Flat removals in ${city.name} — walk-up and lifted flats moved by experienced, insured crews. Instant transparent quote online.`,
    intro:
      "Flat removals are their own discipline. Narrow stairwells, walk-up flats with no lift, tight parking windows, communal-area damage risks and shared loading bays all make flat moves trickier than house moves. Transport Anything 247 provides dedicated flat removals across the UK with crews who specialise in stair work, communal-area protection, and tight-parking logistics.",
    deepDive:
      "Most flat moves are studio or one-bed; many are walk-ups, and a meaningful number are top-floor with no lift. We size the crew accordingly — a four-person crew for top-floor walk-ups makes the move faster and reduces the risk of injury. Communal corridors and stairwells are protected with floor runners and door-edge protectors. Where the building requires a loading-bay slot booking, we coordinate with the building management to secure the window in advance.",
    localIntro: (city) =>
      `Moving flat in ${city.name}? Whether you're upsizing within ${city.name}, downsizing to a flat from a house, or relocating from a flat to another city entirely, Transport Anything 247 provides dedicated flat removals in ${city.name} — including walk-up flats with no lift, communal-area protection, and tight-parking coordination.`,
    howItWorks: [
      {
        title: "Quote with flat details",
        body: "Tell us the flat size, floor number, lift availability and inventory. We size the crew and vehicle for the access reality.",
      },
      {
        title: "Right crew for the access",
        body: "Two-person for ground floor; three or four for upper floors with no lift. Communal-area protection included.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Walk-up flat specialists",
          body: "Top-floor flats with no lift are routine — we know how to do them fast without damaging the staircase.",
        },
        {
          title: "Communal-area protection",
          body: "Floor runners, corner protectors and door-edge guards are included on every flat move to prevent damage to shared areas.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Do you charge extra for walk-up flats?",
        a: "Walk-up flats with no lift typically need a larger crew, which is reflected in the booking quote. The quote is calculated transparently — you see the full price before booking.",
      },
      {
        q: "Will you protect the communal corridors and stairwells?",
        a: "Yes. Floor runners, corner protectors and door-edge guards are included on every flat move as standard.",
      },
      {
        q: "Can you coordinate with building management on loading bays?",
        a: "Yes. We can liaise with building management to book a loading-bay slot in advance. Please share the building name, management contact, and any required forms at booking.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `How common are walk-up flats in ${city.name}?`,
        a: `Walk-up flats are extremely common in ${city.name}, particularly in older Victorian and Edwardian conversions across ${city.region}. Our crews handle them every day and know the typical access patterns.`,
      },
      {
        q: `Can you handle tight-parking situations in ${city.name}?`,
        a: `Yes. Tight parking is part of every flat move in ${city.name}. We bring the right vehicle for the street and coordinate with you to confirm parking suspension or permit requirements at booking.`,
      },
    ],
    related: ["house-removals", "man-and-van", "office-removals", "furniture-delivery"],
    keywordTerms: ["flat removals", "flat moving", "walk-up flat removals", "studio flat moving UK"],
  },

  "office-removals": {
    slug: "office-removals",
    name: "Office Removals",
    silo: "removals",
    icon: "Building2",
    plural: "office moves",
    h1Parent: "Office Removals Across the UK",
    h1Local: (city) => `Office Removals in ${city.name}`,
    titleParent: "Office Removals UK | Out-of-Hours, IT-Safe | Transport Anything 247",
    metaDescParent: "Professional office removals across the UK. Evening and weekend slots, IT-safe handling, transparent instant pricing. Same-day available.",
    metaDescLocal: (city) =>
      `Office removals in ${city.name} — evening and weekend slots, IT-safe handling, full insurance. Instant transparent quote online.`,
    intro:
      "Office removals are a business-critical job — every hour the office is unproductive is an hour of lost revenue. Transport Anything 247 provides dedicated office removals across the UK with experienced crews who plan the move around your business day, deliver out-of-hours when needed, and handle IT and AV equipment with the care it requires.",
    deepDive:
      "Office moves range from a single-room agency relocation to a full corporate HQ move. We handle both extremes. Single-room moves can usually be done in a Saturday morning slot with a two-person crew and a Luton. Multi-floor corporate moves are planned over several days, with crew sizes between four and twelve depending on inventory, and vehicles up to 18-tonne curtain-siders for the bulk furniture run. IT and AV equipment is handled by crews trained in anti-static handling and secure packing. We coordinate directly with your facilities manager, IT lead and office fit-out contractor as needed.",
    localIntro: (city) =>
      `Moving office in ${city.name}? Whether you're relocating across the road within ${city.name}, or moving to another city entirely, Transport Anything 247 provides dedicated office removals in ${city.name} — including evening and weekend slots, IT-safe handling, and direct liaison with facilities and IT teams.`,
    howItWorks: [
      {
        title: "Site visit and quote",
        body: "For multi-floor or multi-day moves we usually visit the office first. Single-floor moves can be quoted instantly from postcodes and inventory description.",
      },
      {
        title: "Out-of-hours scheduling",
        body: "Evening and weekend slots are routine for office moves — they minimise business disruption and usually mean clear access to lifts and loading bays.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Out-of-hours capability",
          body: "Evening and weekend slots are routinely available so your business day stays uninterrupted.",
        },
        {
          title: "IT-safe handling",
          body: "Server racks, monitors, AV kit and printers handled by crews trained in anti-static handling and secure packing.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Can you move us out-of-hours?",
        a: "Yes. Most corporate office moves are done at weekends or in the evening to avoid disrupting the working day. Out-of-hours slots are confirmed at booking.",
      },
      {
        q: "Do you handle IT equipment?",
        a: "Yes. Servers, monitors, AV kit and printers are routine. For larger IT environments we coordinate directly with your IT team to schedule disconnection, transport and reconnection windows.",
      },
      {
        q: "Can you supply packing crates?",
        a: "Yes. Lidded plastic packing crates (preferred for office moves over cardboard) can be delivered in advance and collected after the move. Add this at booking and we will price it in.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Do you cover business addresses across ${city.name}?`,
        a: `Yes. Commercial premises across ${city.name} and ${city.region} are routine. Common business areas around ${city.postcodeOutwardSamples.slice(0, 2).join(" and ")} are well-known to our crews.`,
      },
      {
        q: `Can you handle weekend office moves in ${city.name}?`,
        a: `Yes — weekend slots in ${city.name} are routine and typically the most efficient time to move (buildings are quieter, lifts are free, no working-hours disruption).`,
      },
    ],
    related: ["house-removals", "flat-removals", "office-equipment-delivery", "man-and-van"],
    keywordTerms: ["office removals", "office moving company", "commercial removals UK", "out-of-hours office move"],
  },

  "man-and-van": {
    slug: "man-and-van",
    name: "Man and Van",
    silo: "removals",
    icon: "Truck",
    plural: "man and van jobs",
    h1Parent: "Man and Van Across the UK",
    h1Local: (city) => `Man and Van in ${city.name}`,
    titleParent: "Man and Van UK | Same-Day, Insured, Reliable | Transport Anything 247",
    metaDescParent: "Professional man-and-van service across the UK. Single items to small moves, same-day available, fully insured. Instant transparent quote.",
    metaDescLocal: (city) =>
      `Man and van in ${city.name} — same-day collections, single items to small moves, fully insured. Instant transparent quote online.`,
    intro:
      "Man-and-van is the workhorse of UK transport — small enough to be flexible, large enough to handle a single sofa or a one-bed flat move. Transport Anything 247 provides professional man-and-van service across the UK with vetted, insured drivers, same-day availability, and transparent instant pricing.",
    deepDive:
      "Man-and-van covers everything between a single-parcel courier and a full house removal. Typical jobs: a sofa from Facebook Marketplace, a one-bed flat move, a single piece of furniture from Ikea, a few boxes between properties, a small office relocation. Vehicles range from car-derived vans for small loads through Lutons for whole-flat moves. Single-driver service is standard; two-person crews are dispatched automatically for jobs requiring lifting beyond a single driver's capacity.",
    localIntro: (city) =>
      `Need a man and van in ${city.name}? Whether you're moving a single sofa, doing a small flat move, or running a few boxes between properties, Transport Anything 247 provides dedicated man-and-van in ${city.name} with same-day availability and transparent instant pricing.`,
    howItWorks: [
      {
        title: "Quote in seconds",
        body: "Tell us what you're moving, plus pickup and delivery postcodes. Pricing is instant and transparent — no callbacks.",
      },
      {
        title: "Right van for the job",
        body: "Car-derived van for small loads, Luton for whole-flat moves. Two-person crew dispatched automatically when needed.",
      },
      ...SHARED_HOW_IT_WORKS_TAIL,
    ],
    whyChoose: compose(
      [
        {
          title: "Single items to small moves",
          body: "From one sofa to a one-bed flat — the right vehicle for the load every time.",
        },
        {
          title: "Same-day availability",
          body: "Most jobs booked in the morning can be completed the same day across all major UK metros.",
        },
      ],
      SHARED_WHY_CHOOSE,
    ),
    faqs: [
      {
        q: "Can you do a single-item collection?",
        a: "Yes. Single-item collections — typically Facebook Marketplace, Gumtree or eBay items — are one of our most common job types. We collect from the seller and deliver to you with photo proof.",
      },
      {
        q: "Will you help with lifting?",
        a: "Yes. The driver helps with loading and unloading as standard. For heavy items (sofas, mattresses, large furniture) we dispatch a two-person crew automatically.",
      },
      {
        q: "How fast can you collect?",
        a: "Same-day collections are routinely available across all major UK metros, subject to driver availability at booking. Most jobs booked before midday are collected the same afternoon.",
      },
      ...SHARED_FAQS,
    ],
    localFaqs: (city) => [
      {
        q: `Do you cover every part of ${city.name}?`,
        a: `Yes. Every postcode in ${city.name} and the surrounding ${city.region} area is covered. Common collection areas around ${city.postcodeOutwardSamples.slice(0, 3).join(", ")} are reached daily.`,
      },
      {
        q: `Can you do a marketplace collection in ${city.name}?`,
        a: `Yes. Marketplace pickups (Facebook, Gumtree, eBay) are one of our most-common jobs in ${city.name}. We collect from the seller and deliver to you — you don't need to be at the pickup.`,
      },
    ],
    related: ["house-removals", "flat-removals", "furniture-delivery", "large-item-delivery"],
    keywordTerms: ["man and van", "man with a van", "single item delivery", "small move UK"],
  },
};

export const SERVICE_LIST = Object.values(SERVICES);

export function getService(slug) {
  return SERVICES[slug] || null;
}

export function getServicesBySilo(siloKey) {
  return SERVICE_LIST.filter((s) => s.silo === siloKey);
}
